import { ReactNode, useContext, useMemo } from "react";
import { AppContext } from "context/appContext";
import { Outlet, useOutletContext } from "react-router";
import { useTranslation } from "react-i18next";
import FormTabNavigation, {
  FormTab,
} from "components/layout/FormTabNavigation";
import { ROUTES, routeWithParams } from "router/routes";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import FormHeaderBar from "components/layout/FormHeaderBar";
import { PanelRootContext } from "types/PanelRootContext";
import { PropertyGroup } from "types";

export enum PropertyGroupDetailsTabs {
  DETAILS = "details",
  BRANDING = "branding",
  FILES = "files",
  QR_CODES = "qrCodes",
  DNT = "doNotTow",
}

export const PropertyGroupDetailsLayout = (): ReactNode => {
  const { t } = useTranslation();
  const { state } = useContext(AppContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const { formMode, setFormMode, displayMode, entity, setEntity } =
    useOutletContext<PanelRootContext<PropertyGroup>>();

  const tabs: FormTab[] = useMemo(() => {
    const _tabs: FormTab[] = [
      {
        label: t("details"),
        route: routeWithParams(ROUTES.PROPERTY_GROUP, [
          state.tenant.id,
          entity.id,
        ]),
        beforeNavigate: () => {
          setFormMode("none");
          return true;
        },
      },
      {
        label: t("donottow.label_plural", { ns: "entities" }),
        route: routeWithParams(ROUTES.PROPERTY_GROUP_DO_NOT_TOW, [
          state.tenant.id,
          entity.id,
        ]),
        beforeNavigate: () => {
          setFormMode("none");
          return true;
        },
      },
      {
        label: t("file.label_plural", { ns: "entities" }),
        route: routeWithParams(ROUTES.PROPERTY_GROUP_FILES, [
          state.tenant.id,
          entity.id,
        ]),
        beforeNavigate: () => {
          setFormMode("none");
          return true;
        },
      },
      {
        label: t("qrCodes.label", { ns: "entities" }),
        route: routeWithParams(ROUTES.PROPERTY_GROUP_QR_CODES, [
          state.tenant.id,
          entity.id,
        ]),
        beforeNavigate: () => {
          setFormMode("none");
          return true;
        },
      },
    ];
    if (!isMobile) {
      _tabs.push({
        label: t("branding"),
        route: routeWithParams(ROUTES.PROPERTY_GROUP_BRANDING, [
          state.tenant.id,
          entity.id,
        ]),
        beforeNavigate: () => {
          setFormMode("none");
          return true;
        },
      });
    }
    return _tabs;
  }, [displayMode, entity, isMobile]);

  return (
    <Box
      flexGrow={1}
      display={"flex"}
      flexDirection={"column"}
      minHeight={0}
      sx={{
        overflow: "hidden",
      }}
    >
      <FormHeaderBar />
      {formMode === "none" && (
        <FormTabNavigation tabs={tabs} title={entity.name} />
      )}
      <Box
        flexGrow={1}
        minHeight={0}
        sx={{
          overflow: "hidden",
        }}
      >
        <Outlet
          context={
            {
              formMode,
              setFormMode,
              entity,
              setEntity,
              displayMode,
            } satisfies PanelRootContext<PropertyGroup>
          }
        />
      </Box>
    </Box>
  );
};

PropertyGroupDetailsLayout.displayName = "PropertyGroupDetailsLayout";
export default PropertyGroupDetailsLayout;
